import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Loader } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  getGetApiCompaniesCurrentServicemodulesTextsServiceModuleIdQueryKey,
  useGetApiCompaniesCurrentServicemodulesTextsServiceModuleId,
  usePatchApiCompaniesCurrentServicemodulesTextsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleModel, TextServiceModuleModel } from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { AutoTranslateAllButton } from '@/components/common/translations/AutoTranslateAllButton'
import { triggerAutoTranslation, useResolvedLanguages } from '@/components/common/translations/useAutoTranslate'
import type { PlannedLanguages } from '@/components/common/translations/types'
import {RichTextEditor} from "@/components/common/RichTextEditor.tsx";

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

const TRANSLATABLE_PROPERTIES = ['text']

/**
 * Editor for Text modules: standard fields (column 1) + the text content (column 2).
 * Saves everything in a single request via the Text endpoint (full update model).
 */
export function EditTextServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-text', 'service-modules'])
  const std = useStandardServiceModuleFields(module)
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetApiCompaniesCurrentServicemodulesTextsServiceModuleId(module.id)
  const patch = usePatchApiCompaniesCurrentServicemodulesTextsServiceModuleId()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ServiceModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })
  const languages = useResolvedLanguages()

  const detail = ((data as any)?.data ?? data) as TextServiceModuleModel | undefined

  const [text, setText] = useState('')
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (detail && !loaded) {
      setText(detail.text ?? '')
      setLoaded(true)
    }
  }, [detail, loaded])

  const [textPlanned, setTextPlanned] = useState<PlannedLanguages>(new Set())

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: { ...std.buildBasePayload(), text: text || null },
      })
      await std.saveTranslations()
      await translations.save(String(module.id))

      const allLanguageCodes = languages.map((l) => l.code)
      triggerAutoTranslation({
        entityType: 'ServiceModule',
        entityId: String(module.id),
        properties: [
          {
            property: 'name',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...std.namePlannedLanguages],
            existingTranslations: std.nameTranslations,
          },
          {
            property: 'text',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...textPlanned],
            existingTranslations: translations.get('text'),
          },
        ],
      })

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesTextsServiceModuleIdQueryKey(module.id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
        }),
      ])
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="module-text">{t('editText.textLabel')}</Label>
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-muted-foreground">
          <Loader className="size-4 animate-spin text-primary" />
          <span className="text-sm">{t('editText.loading')}</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <RichTextEditor
            id="module-text"
            value={text}
            onChange={setText}
            placeholder={t('editText.textPlaceholder')}
            className="min-w-0 flex-1"
            contentClassName="min-h-64"
            disabled={false}
          />
          <TranslateButton
            dialogTitle={t('editText.textTranslateTitle')}
            value={translations.get('text')}
            onChange={(v) => translations.set('text', v)}
            plannedLanguages={textPlanned}
            onPlannedChange={setTextPlanned}
            autoTranslateAll={autoTranslateAll}
            sourceValue={text}
            isRichText
          />
        </div>
      )}
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editText.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={std} moduleType={module.type} autoTranslateAll={autoTranslateAll}/>}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending || !loaded}
      autoTranslateButton={
        <AutoTranslateAllButton
          properties={[
            {
              property: 'name',
              sourceValue: std.name,
              existingTranslations: std.nameTranslations,
              manualTranslations: {},
              plannedLanguages: std.namePlannedLanguages,
              onPlannedChange: std.setNamePlannedLanguages,
            },
            {
              property: 'text',
              sourceValue: text,
              existingTranslations: translations.get('text'),
              manualTranslations: {},
              plannedLanguages: textPlanned,
              onPlannedChange: setTextPlanned,
            },
          ]}
          autoTranslateAll={autoTranslateAll}
          onAutoTranslateAllChange={setAutoTranslateAll}
        />
      }
    />
  )
}
